import React from "react";
import { Box, Text } from "@salestrak/ui";

import type { EntityVisualSize } from "./entity-visual";
import type { Omittable } from "@salestrak/ui";

const LABEL_TEXT_SIZE: Record<EntityVisualSize, "2xs" | "xs" | "sm" | "base" | "lg"> = {
  "2xs": "2xs",
  xs: "xs",
  sm: "sm",
  md: "base",
  lg: "base",
  xl: "lg",
};

export type EntityTagBodyProps = Omittable<{
  label: string;
  visual?: React.ReactNode;
  display: "full" | "visual" | "label";
  weight: "regular" | "medium" | "semibold";
  size: EntityVisualSize;
  subtitle?: string | null;
  /** A secondary line rendered BELOW the label (stacked), e.g. a search
   *  result's address. Distinct from `subtitle`, which renders inline. */
  description?: string | null;
  mutedLabel?: boolean;
  /** `"text"` = the chip sits inline inside a sentence Text (see EntityTag). */
  as?: "box" | "text";
}>;

// The chip's "regular" weight maps to the text scale's "normal".
const TEXT_WEIGHT: Record<EntityTagBodyProps["weight"], "normal" | "medium" | "semibold"> = {
  regular: "normal",
  medium: "medium",
  semibold: "semibold",
};

function EntityTagBodyComponent({
  label,
  visual,
  display,
  weight,
  size,
  subtitle,
  description,
  mutedLabel,
  as = "box",
}: EntityTagBodyProps) {
  if (display === "visual") return <>{visual}</>;

  // Inline label (as="text") flows within a wrapping sentence, so it must stay
  // an inline span: `truncate` maps to a block line-clamp that would force a
  // line break before and after the chip, and `leading="none"` would desync
  // its line box from the surrounding text.
  const inline = as === "text";

  const labelText = (
    <Text
      size={LABEL_TEXT_SIZE[size]}
      weight={TEXT_WEIGHT[weight]}
      variant={mutedLabel ? "muted" : undefined}
      truncate={inline ? undefined : 1}
      // Pin line box to glyph height so the shell's ghost-wash bleed (fixed
      // negative margin) lands predictably and the chip height matches the text.
      leading={inline ? undefined : "none"}
    >
      {label}
    </Text>
  );

  if (display === "label") return labelText;

  if (description) {
    return (
      <>
        {visual}
        <Box shrink direction="column" gap="2xs" minWidth={0}>
          {labelText}
          <Text size="xs" variant="muted" truncate={1} leading="none">
            {description}
          </Text>
        </Box>
      </>
    );
  }

  if (subtitle) {
    return (
      <>
        {visual}
        <Box shrink direction="row" align="baseline" gap="2xs">
          {labelText}
          <Text
            size={LABEL_TEXT_SIZE[size]}
            variant="muted"
            truncate={1}
            leading="none"
          >
            {`· ${subtitle}`}
          </Text>
        </Box>
      </>
    );
  }

  return (
    <>
      {visual}
      <Box shrink direction="row" align="center" minWidth={0}>
        {labelText}
      </Box>
    </>
  );
}

EntityTagBodyComponent.displayName = "EntityTagBody";

export const EntityTagBody = React.memo(EntityTagBodyComponent);
